import { Link, Navigate, Outlet, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { LanguageSwitch } from '@/components/LanguageSwitch';
import { Button } from '@/components/ui';
import { ApiRequestError } from '@/lib/api';
import { useAdminMe, useLogout } from '@/lib/queries';

/** Tabs; `match` also covers the pages under a tab (a registration under the list). */
const NAV = [
  {
    to: '/admin',
    label: 'admin.nav.dashboard',
    match: (p: string) => p === '/admin' || p === '/admin/',
  },
  {
    to: '/admin/registrations',
    label: 'admin.nav.registrations',
    match: (p: string) => p.startsWith('/admin/registrations'),
  },
  {
    to: '/admin/documents',
    label: 'admin.nav.documents',
    match: (p: string) => p.startsWith('/admin/documents'),
  },
  {
    to: '/admin/formats',
    label: 'admin.nav.formats',
    match: (p: string) => p.startsWith('/admin/formats'),
  },
  {
    to: '/admin/vouchers',
    label: 'admin.nav.vouchers',
    match: (p: string) => p.startsWith('/admin/vouchers'),
  },
  {
    to: '/admin/teams',
    label: 'admin.nav.teams',
    match: (p: string) => p.startsWith('/admin/teams'),
  },
  {
    to: '/admin/settings',
    label: 'admin.nav.settings',
    match: (p: string) => p.startsWith('/admin/settings'),
  },
];

/**
 * Back-office shell (PRD §6): desktop layout, gated by the event's admin allow-list. A visitor
 * without a session is sent to the login screen, which returns here after the magic link.
 */
export function AdminLayout() {
  const { t } = useTranslation();
  const me = useAdminMe();
  const logout = useLogout();
  const qc = useQueryClient();
  const { pathname } = useLocation();

  if (me.isLoading) return <p className="p-10 text-center text-ink-muted">…</p>;
  const status = me.error instanceof ApiRequestError ? me.error.status : null;
  if (status === 401) return <Navigate to="/login?redirect=/admin" replace />;

  const signOut = async () => {
    await logout.mutateAsync();
    await qc.invalidateQueries({ queryKey: ['me'] });
    qc.removeQueries({ queryKey: ['admin'] });
  };

  return (
    <div className="flex min-h-dvh flex-col">
      <header className="border-b border-line bg-paper">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-6 py-3">
          <Link
            to="/admin"
            className="font-display text-sm font-bold uppercase tracking-[0.12em] text-ink no-underline hover:text-ink"
          >
            {t('app.name')} · {t('admin.title')}
          </Link>
          {me.data && (
            <nav className="flex flex-1 gap-5 text-sm font-semibold">
              {NAV.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  aria-current={n.match(pathname) ? 'page' : undefined}
                  className={`no-underline ${n.match(pathname) ? 'text-ink' : 'text-ink-muted hover:text-ink'}`}
                >
                  {t(n.label)}
                </Link>
              ))}
            </nav>
          )}
          <div className="flex items-center gap-4 text-sm text-ink-muted">
            {me.data && <span>{t('admin.signedInAs', { email: me.data.email })}</span>}
            <LanguageSwitch />
            <Button variant="ghost" size="md" className="w-auto!" onClick={signOut}>
              {t('me.logout')}
            </Button>
          </div>
        </div>
      </header>
      <main className="mx-auto w-full max-w-6xl flex-1 px-6 py-8">
        {status === 403 || !me.data ? (
          <section className="mx-auto max-w-md space-y-3 pt-10 text-center">
            <h1 className="font-display text-section font-bold">{t('admin.forbiddenTitle')}</h1>
            <p className="text-ink-muted">{t('admin.forbiddenBody')}</p>
          </section>
        ) : (
          <Outlet />
        )}
      </main>
    </div>
  );
}
